import React from 'react';

import {MuiThemeProvider, Theme as MuiTheme} from '@material-ui/core/styles';

import darkmodejs from '@assortment/darkmodejs';

import {CreateDarkTheme, CreateLightTheme, DualVariantTheme} from "../Utils/CreateTheme";

const ThemeSettingsContext = React.createContext<Partial<ThemeSettingsContextProps>>({});

type NonEmptyArray<T> = [T, ...T[]];

enum Theme {
  DARK = 'dark',
  LIGHT = 'light',
  NO_PREF = 'no-preference',
  NO_SUPP = 'no-support',
}

const LOCALSTORAGE_KEY_THEMEPREFERENCES = 'themePreferences';
export interface LocalStorageThemePreferences {
  name: string;
  mode: PreferenceModes;
}

const DEFAULT_THEME_KEY = 'Default';
export enum PreferenceModes {
  AUTO = 'Auto',
  LIGHT = "Light",
  DARK = "Dark",
}

const DEFAULT_THEME_OPTION: Readonly<DualVariantTheme> = {
  name: DEFAULT_THEME_KEY,
  lightTheme: CreateLightTheme(),
  darkTheme: CreateDarkTheme(),
};

const DEFAULT_LOCALSTORAGE_PREFERENCES: LocalStorageThemePreferences = {
  name: DEFAULT_THEME_KEY,
  mode: PreferenceModes.AUTO,
};

interface DarkModeJS {
  removeListeners: () => void;
}

export interface ThemeSettingsProviderProps {
  themeOptions: Readonly<NonEmptyArray<Readonly<DualVariantTheme>>>;
}

interface ThemeSettingsProviderState {
  theme: Readonly<DualVariantTheme>;
  osMode: Theme | '';
}

export interface ThemeSettingsContextProps {
  themeOptions: string[];
  changeTheme: (e: any) => void;
  getStoredThemePreferences(): LocalStorageThemePreferences;
}

class ThemeSettingsProvider extends React.Component<ThemeSettingsProviderProps, ThemeSettingsProviderState> {

  /**
   * Stringifies the {@link LocalStorageThemePreferences} preferences object to {@link localStorage}
   * @param theme {@link LocalStorageThemePreferences} The theme preferences object to store
   */
  private static storeThemePreference = (theme: Readonly<LocalStorageThemePreferences>): Readonly<LocalStorageThemePreferences> => {
    try {
      localStorage.setItem(LOCALSTORAGE_KEY_THEMEPREFERENCES, JSON.stringify(theme));
    } catch (e) {

    }
    return theme;
  };

  private dmjs: DarkModeJS;

  static defaultProps: ThemeSettingsProviderProps = {
    themeOptions: [DEFAULT_THEME_OPTION],
  };

  constructor(props: ThemeSettingsProviderProps) {
    super(props);
    const { themeOptions } = props;
    this.state = {
      theme: themeOptions[0] ? themeOptions[0] : DEFAULT_THEME_OPTION,
      osMode: ''
    };
  }

  componentDidMount = () => {
    this.dmjs && this.dmjs.removeListeners();
    this.dmjs = darkmodejs({
      onChange: (activeTheme: Theme, themes: typeof Theme) => {
        const stateToApply: ThemeSettingsProviderState = {
          theme: this.getThemeToApply(),
          osMode: Theme.LIGHT,
        };
        if (activeTheme === themes.DARK) {
          stateToApply.osMode = Theme.DARK;
        }
        this.setState(stateToApply);
      }
    });
  };

  componentDidUpdate(prevProps: Readonly<ThemeSettingsProviderProps>, prevState: Readonly<ThemeSettingsProviderState>, snapshot?: any) {
    const { themeOptions } = this.props;
    if (prevProps.themeOptions !== themeOptions && themeOptions[0]) {
      this.setState({ theme: themeOptions[0]})
    }
  }

  componentWillUnmount(): void {
    this.dmjs && this.dmjs.removeListeners();
  }

  private getThemeToApply(): DualVariantTheme {
    const { themeOptions } = this.props;
    const preference: LocalStorageThemePreferences = ThemeSettingsProvider.getThemePreference();
    const themeToApply = themeOptions.find((option: Readonly<DualVariantTheme>) => option.name === preference.name);
    if (themeToApply) {
      return themeToApply;
    } else if (themeOptions.length > 0) {
      return themeOptions[0];
    } else {
      return DEFAULT_THEME_OPTION;
    }
  }


  private static getThemePreference = (): LocalStorageThemePreferences => {
    try {
      const storedPreferences: string | null = localStorage.getItem(LOCALSTORAGE_KEY_THEMEPREFERENCES);
      if (!storedPreferences) {
        return DEFAULT_LOCALSTORAGE_PREFERENCES;
      }
      return JSON.parse(storedPreferences) as LocalStorageThemePreferences;
    } catch (e) {
      return DEFAULT_LOCALSTORAGE_PREFERENCES;
    }
  };

  onRequestChangeTheme = (value: LocalStorageThemePreferences): void => {
    ThemeSettingsProvider.storeThemePreference(value);
    this.setState({ theme: this.getThemeToApply()});
  };


  render() {
    const { children, themeOptions } = this.props;
    const { theme, osMode } = this.state;
    const themePreference = ThemeSettingsProvider.getThemePreference();
    let themeToPushDown: MuiTheme;
    switch (themePreference.mode) {
      case PreferenceModes.DARK:
        themeToPushDown = theme.darkTheme;
        break;
      case PreferenceModes.LIGHT:
        themeToPushDown = theme.lightTheme;
        break;
      default:
      case PreferenceModes.AUTO:
        themeToPushDown = osMode === Theme.DARK ? theme.darkTheme : theme.lightTheme;
    }
    return (
      <ThemeSettingsContext.Provider
        value={{
          themeOptions: themeOptions.map((option: DualVariantTheme) => option.name),
          getStoredThemePreferences: ThemeSettingsProvider.getThemePreference,
          changeTheme: this.onRequestChangeTheme,
        }}
      >
        {
          themeToPushDown && (
            <MuiThemeProvider theme={themeToPushDown}>
              {children}
            </MuiThemeProvider>
          )}
      </ThemeSettingsContext.Provider>
    );
  }
}

export const ThemeSettingsConsumer = ThemeSettingsContext.Consumer;

export const withThemeSettings = (WrappedComponent: any) => {
  return class extends React.Component<any, any> {
    render() {
      return (
        <ThemeSettingsConsumer>
          {({ themeOptions, changeTheme, getStoredThemePreferences }) => (
            <WrappedComponent
              {...this.props}
              themeOptions={themeOptions}
              changeTheme={changeTheme}
              getStoredThemePreferences={getStoredThemePreferences}
            />
          )}
        </ThemeSettingsConsumer>
      );
    }
  };
};

export default ThemeSettingsProvider;
